import React, { useMemo } from 'react';
import { Button, Card, CountChip, ListEmptyState, TableIcon } from '../../components/ui';
import { cn } from '../../lib/utils';
import { EFormReportChart } from './ReportCharts';
import { formatPivotCell, pivotReport } from './reportPivot';
import type { EFormReportChartType, EFormReportQueryResult } from './useData';

export type ReportPivotResultProps = {
  result: EFormReportQueryResult | null;
  chartType: EFormReportChartType;
  showTotals?: boolean;
  running?: boolean;
  emptyHint?: string;
  toolbar?: React.ReactNode;
  /** Optional slot to the left of the table (e.g. row axis chips in the builder). */
  leadingSlot?: React.ReactNode;
  showRowCount?: boolean;
  onCellClick?: (row: Record<string, unknown>, colEquals?: Record<string, unknown>) => void;
};

const ReportPivotResult: React.FC<ReportPivotResultProps> = ({
  result,
  chartType,
  showTotals = true,
  running,
  emptyHint = 'No pivot rows yet.',
  toolbar,
  leadingSlot,
  showRowCount = true,
  onCellClick,
}) => {
  const display = useMemo(
    () => (result ? pivotReport(result, { showTotals }) : null),
    [result, showTotals],
  );

  const rowCount = display?.rows.filter((r) => !r.__total).length ?? 0;

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {toolbar || showRowCount ? (
        <div className="flex flex-wrap items-center gap-3">
          {toolbar}
          {showRowCount ? <CountChip>{rowCount} rows</CountChip> : null}
        </div>
      ) : null}

      {chartType !== 'table' && result ? (
        <Card className="p-3">
          <EFormReportChart chartType={chartType} columns={result.columns} rows={result.rows} />
        </Card>
      ) : null}

      <div className={cn('flex flex-col gap-3', leadingSlot ? 'md:flex-row' : undefined)}>
        {leadingSlot}
        <Card className="min-w-0 flex-1 overflow-x-auto">
          {display?.rows.length ? (
            <table className="w-full table-fixed text-sm">
              <thead>
                {display.measureCount > 1 && display.columnGroups.length ? (
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700">
                    {display.columnGroups.map((g) => (
                      <th
                        key={g.key}
                        colSpan={g.span}
                        className="px-3 py-2 text-center align-middle font-semibold whitespace-nowrap"
                      >
                        {g.label}
                      </th>
                    ))}
                  </tr>
                ) : null}
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700">
                  {display.columns.map((c) => (
                    <th
                      key={c.key}
                      className="px-3 py-2 text-center align-middle font-semibold whitespace-nowrap"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {display.rows.map((row, ri) => (
                  <tr
                    key={ri}
                    className={cn(
                      'border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/40',
                      Boolean(row.__total) && 'font-semibold',
                    )}
                  >
                    {display.columns.map((c) => (
                      <td
                        key={c.key}
                        className="px-3 py-2 text-center align-middle tabular-nums whitespace-nowrap"
                      >
                        {c.kind === 'measure' && !row.__total && onCellClick ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="mx-auto h-auto min-h-0 justify-center px-0 py-0"
                            onClick={() => onCellClick(row, c.colEquals)}
                          >
                            {formatPivotCell(row[c.key])}
                          </Button>
                        ) : (
                          formatPivotCell(row[c.key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <ListEmptyState
              icon={<TableIcon className="h-12 w-12" />}
              title={running ? 'Running report…' : 'No pivot rows yet'}
              hint={emptyHint}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default ReportPivotResult;
